import React from 'react';
import Hidden from '@material-ui/core/Hidden';
import Divider from '@material-ui/core/Divider';
import { TablePagination } from './Pagination';
import * as S from './TableStyles';
import type { TableRowPropsType } from './TableStyles';
import { IUseTable } from '../../models/Table';
import { PercentLabel } from '../PercentLabel';
import { Card, CardTitle, NewCardContainer } from '../Card';
import styled, { css } from 'styled-components';

export type TableMobileType = 'generic' | 'stats' | 'card';
export type ICalculateStatsStatus = 'pass' | 'fail' | 'none';

type SimpleTableProps<T> = {
  data: IUseTable<T>;
  keyId: string;
  rowAction?: (row: T) => React.ReactNode;
  onClick?: (row: T) => void;
  calculateStatsStatus?: (row: T) => ICalculateStatsStatus;
  hasBorder?: boolean;
  roundedTop?: boolean;
  roundedBottom?: boolean;
  mobileView?: TableMobileType;
};

const renderTable = <T,>({ data, mobileView, rowAction, keyId, onClick, calculateStatsStatus }: SimpleTableProps<T>) => {
  const { response: rows, fieldsConfig } = data;

  const fieldsConfigMatchInfo = fieldsConfig.filter((item) => !item.percent);
  const fieldsConfigMatchData = fieldsConfig.filter((item) => item.percent);

  return (
    <S.TableCustom>
      <Hidden mdDown>
        <S.TableHeadCustom>
          <S.TableRowCustom>
            {fieldsConfig.map(({ label, id, width }) => (
              <S.TableHeaderCustom key={id} scope="col" width={width ? `${width}px` : 'unset'}>
                {label}
              </S.TableHeaderCustom>
            ))}
            {rowAction && <S.TableHeaderCustom></S.TableHeaderCustom>}
          </S.TableRowCustom>
        </S.TableHeadCustom>
      </Hidden>

      <S.TableBodyCustom>
        {rows.map((row) => (
          <S.TableRowCustom
            key={row[keyId]}
            highlightRow="odd"
            isHover={!!onClick}
            statsStatus={calculateStatsStatus ? calculateStatsStatus(row) : 'none'}
            onClick={() => {
              onClick && onClick(row);
            }}
          >
            <Hidden mdDown>
              {fieldsConfig.map(({ label, id }) => (
                <S.TableCellCustom key={label}>{row[id]}</S.TableCellCustom>
              ))}
            </Hidden>

            {mobileView === 'generic' ? (
              <Hidden lgUp>
                {fieldsConfig.map(({ label, id }) => (
                  <S.TableCellCustom key={label}>
                    <S.TableCellLabel>{label}</S.TableCellLabel>
                    {row[id]}
                  </S.TableCellCustom>
                ))}
              </Hidden>
            ) : null}
            {mobileView === 'stats' ? (
              <Hidden lgUp>
                <>
                  <S.TableHalfCell>
                    {fieldsConfigMatchInfo.map(({ label, id }) => (
                      <S.TableCellStats as="div" key={id}>
                        <S.TableCellLabel>{label}</S.TableCellLabel>
                        {row[id]}
                      </S.TableCellStats>
                    ))}
                  </S.TableHalfCell>
                  <S.TableHalfCell>
                    {fieldsConfigMatchData.map(({ label, id }) => (
                      <S.Match as="div" key={id}>
                        <S.MatchDataContainer>{label}</S.MatchDataContainer>
                        <S.MatchDataContainerWithBorder>
                          <PercentLabel value={row[id]} helperText={row[`${id}LastCount`]}>
                            {row[id]}
                          </PercentLabel>
                        </S.MatchDataContainerWithBorder>
                      </S.Match>
                    ))}
                  </S.TableHalfCell>
                </>
              </Hidden>
            ) : null}

            {rowAction && <S.TableCellAction align="right">{rowAction(row)}</S.TableCellAction>}
          </S.TableRowCustom>
        ))}
      </S.TableBodyCustom>
    </S.TableCustom>
  );
};

const FlexColumn = styled.div<{ isHover?: boolean; statsStatus?: TableRowPropsType['statsStatus'] }>`
  display: flex;
  flex-direction: column;
  padding: 0.5rem 1rem;
  position: relative;

  ${({ isHover }) => isHover && S.hoverTableItemMixin}

  ${({ statsStatus }) =>
    statsStatus &&
    statsStatus !== 'none' &&
    css`
      &:after {
        content: '';
        width: 3px;
        background-color: ${statsStatus === 'pass' ? '#C4F5E8' : '#F7E5D2'};
        height: 90%;
        position: absolute;
        left: 0;
        top: 0;
      }
    `}
`;

const Flex = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const StyledDivider = styled(Divider)`
  margin: 0.5rem 0;
`;
const renderCard = <T,>({ data, mobileView, rowAction, keyId, onClick, calculateStatsStatus }: SimpleTableProps<T>) => {
  const { response: rows, fieldsConfig } = data;

  const fieldsConfigMatchInfo = fieldsConfig.filter((item) => !item.percent && !item.cardTitle);
  const fieldsConfigMatchData = fieldsConfig.filter((item) => item.percent);

  const cardTitleId = fieldsConfig.filter((item) => item.cardTitle)?.[0]?.id || 'league';

  return (
    <>
      <Hidden mdDown>
        {renderTable({
          data,
          mobileView,
          rowAction,
          keyId,
          onClick,
          calculateStatsStatus,
        })}
      </Hidden>
      <Hidden lgUp>
        {rows.map((row) => (
          <NewCardContainer key={row[keyId]}>
            <CardTitle>{row[cardTitleId]}</CardTitle>
            <FlexColumn
              isHover={!!onClick}
              onClick={() => {
                onClick && onClick(row);
              }}
              statsStatus={calculateStatsStatus ? calculateStatsStatus(row) : 'none'}
            >
              <Flex>
                {fieldsConfigMatchInfo.map(({ label, id }) => (
                  <S.TableCellStats withoutBorder as="div" key={id}>
                    <S.TableCellLabel>{label}</S.TableCellLabel>
                    {row[id]}
                  </S.TableCellStats>
                ))}
              </Flex>
              <StyledDivider />
              <Flex>
                {fieldsConfigMatchData.map(({ label, id }) => (
                  <S.TableCellStats withoutBorder as="div" key={id}>
                    <S.TableCellLabel>{label}</S.TableCellLabel>
                    {row[id]}
                  </S.TableCellStats>
                ))}
                {rowAction && (
                  <S.TableCellStats isAction withoutBorder as="div" key={`${row[keyId]}-action}`}>
                    {rowAction(row)}
                  </S.TableCellStats>
                )}
              </Flex>
            </FlexColumn>
          </NewCardContainer>
        ))}
      </Hidden>
    </>
  );
};
export const SimpleTable = <T,>({
  data,
  keyId = 'id',
  rowAction,
  onClick,
  hasBorder,
  roundedBottom = true,
  roundedTop = true,
  mobileView = 'generic',
  calculateStatsStatus,
}: SimpleTableProps<T>) => {
  return (
    <S.SimpleTableContainer hasBorder={hasBorder} roundedTop={roundedTop} roundedBottom={roundedBottom}>
      <S.TableContainerCustom>
        {mobileView === 'card'
          ? renderCard({
              data,
              keyId,
              mobileView,
              rowAction,
              onClick,
              calculateStatsStatus,
            })
          : renderTable({
              data,
              keyId,
              mobileView,
              rowAction,
              onClick,
              calculateStatsStatus,
            })}
      </S.TableContainerCustom>
      <TablePagination
        count={data.rowsCount}
        rowsPerPage={data.rowsPerPage}
        page={data.page}
        setPage={data.setPage}
        setRowsPerPage={data.setRowsPerPage}
      />
    </S.SimpleTableContainer>
  );
};
